// A deploy replaces the hashed /assets/* of the previous build. A tab that was
// open across the deploy still holds the OLD index.html, so its next lazy
// route/component import asks for a chunk that no longer exists and the
// browser throws «Failed to fetch dynamically imported module». The fix is
// not to keep old chunks around but to reload ONCE: the fresh index.html
// points at the live chunks. A guard keeps a genuinely broken build from
// reloading in a loop — a second failure at the same location within a
// minute shows the error page instead.

const CHUNK_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i, // Chrome, Edge
  /importing a module script failed/i, // Safari
  /error loading dynamically imported module/i, // Firefox
  /loading (css )?chunk [\w-]+ failed/i, // webpack-style wording, kept for safety
  /unable to preload css/i, // Vite's CSS preload helper
];

export function isChunkLoadError(err: unknown): boolean {
  const msg =
    err instanceof Error
      ? err.message
      : typeof err === 'string'
        ? err
        : err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : '';
  return CHUNK_ERROR_PATTERNS.some((p) => p.test(msg));
}

export const CHUNK_RELOAD_KEY = 'ehz:chunk-reload';
/** A second failure at the same location inside this window means the build itself is broken. */
export const CHUNK_RELOAD_WINDOW_MS = 60_000;

function sessionStore(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

export interface ReloadOnceDeps {
  location?: string;
  storage?: Storage | null;
  now?: () => number;
  reload?: () => void;
}

/**
 * Reload the page to pick up the current build, at most once per location per
 * minute. Returns true when a reload was triggered, false when one already
 * happened for this location recently (the caller then shows the error page
 * with a manual reload) or when no guard storage is available (never loop blind).
 */
export function reloadOnceForChunkError(deps: ReloadOnceDeps = {}): boolean {
  const location = deps.location ?? window.location.pathname;
  const storage = deps.storage === undefined ? sessionStore() : deps.storage;
  const now = deps.now ?? Date.now;
  const reload = deps.reload ?? (() => window.location.reload());
  if (!storage) return false;
  try {
    const prev = storage.getItem(CHUNK_RELOAD_KEY);
    if (prev) {
      const sep = prev.lastIndexOf('|');
      const prevLoc = sep < 0 ? prev : prev.slice(0, sep);
      const prevAt = sep < 0 ? 0 : Number(prev.slice(sep + 1));
      if (prevLoc === location && now() - prevAt < CHUNK_RELOAD_WINDOW_MS) {
        storage.removeItem(CHUNK_RELOAD_KEY);
        return false;
      }
    }
    storage.setItem(CHUNK_RELOAD_KEY, `${location}|${now()}`);
  } catch {
    return false;
  }
  reload();
  return true;
}
